import {ReactElement, useState} from "react";
import {PropertyType, useCreateManualPropertyMutation} from "../../../__generated__/generated_types";
import {Close, Modal} from "@zendeskgarden/react-modals";
import {toast} from "react-toastify";
import ManualPropertyForm, {TManualPropertyRecord} from "./components/manual-property-form/ManualPropertyForm";
import {ThemeProvider} from "styled-components";
import {ViziblyTheme} from "../../../styles/zendesk-garden/ViziblyZDGTheme";
import {ManualProperty, PMSProperty} from "./logic/types";

export interface ICreateManualProperty {
    formVisible: boolean,
    isStandard: boolean,
    pmsPropertiesData: PMSProperty[] | undefined;
    existingPropertiesData: ManualProperty[] | undefined;
    onModalClose: () => void,
    onCreate: () => void,
    budgetYear: number
}

export function ManualPropertyCreateModal(props: ICreateManualProperty): ReactElement {
    const {
        pmsPropertiesData,
        formVisible,
        isStandard,
        onModalClose,
        onCreate
    } = props;

    const [createManualProperty, { loading: createLoading }] = useCreateManualPropertyMutation({
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data && data.createManualProperty) {
                toast.success(`Manual property "${data.createManualProperty.name}" created`);
                onCreate();
            }
            reset();
        },
    });

    const [manualProperty, setManualProperty] = useState<TManualPropertyRecord>({
        name: "",
        systemName: "",
        isSystemNameSameAsName: true,
        isDeactivated: false, // Keep this property as it's part of the TManualPropertyRecord type
        reforecastStartMonthIndex: 0,
    });

    function updateManualProperty(update: Partial<TManualPropertyRecord>): void {
        setManualProperty({
            ...manualProperty,
            ...update,
        });
    }

    function doSaveManualProperty(): void {
        createManualProperty({
            variables: {
                name: manualProperty.name,
                propertyType: isStandard ? PropertyType.Standard : PropertyType.Manual,
                reforecastStartMonthIndex: manualProperty.reforecastStartMonthIndex,
                budgetYear: props.budgetYear
            }
        }).catch(() => { /* Error handling is done via onError in Apollo client */ });
    }

    function reset(): void {
        setManualProperty({
            name: "",
            systemName: "",
            isDeactivated: false, // Keep this property as it's part of the TManualPropertyRecord type
            reforecastStartMonthIndex: 0,
        });
        onModalClose();
    }

    if (!formVisible) {
        return <></>;
    }

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <Modal onClose={reset} isLarge style={{minWidth: "80rem"}}>
                <ManualPropertyForm
                    isCreate
                    isStandard={isStandard}
                    manualProperty={manualProperty}
                    loading={createLoading}
                    onChange={updateManualProperty}
                    onSubmit={doSaveManualProperty}
                    onClose={reset}
                    pmsPropertiesData={pmsPropertiesData}
                    existingProperties={props.existingPropertiesData}
            />
                <Close aria-label="Close modal" />
            </Modal>
        </ThemeProvider>
    );
}
